/**
 * ConnectionsPanel — end-of-dossier "Continue Your Investigation" module.
 *
 * Renders the multi-layer connection graph produced by
 * `scripts/backfill_connections.py`. Each layer is a small horizontal
 * strip so a user always sees at least ONE path forward:
 *
 *   • Continue Your Investigation  (top 4 related dossiers)
 *   • Timeline Connections          (adjacent-era events in other dossiers)
 *   • Key People                    (people who appear in ≥2 dossiers)
 *   • Organizations                 (agencies/orgs that cross-appear)
 *
 * Data source: `editorial_modules.connections` on the topic doc.
 * Legacy dossiers without a `connections` block gracefully render nothing;
 * the older `RelatedDossiersSection` remains as their fallback.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Building2, Clock, Compass } from "lucide-react";

const RailHeader = ({ icon: Icon, title, subtitle, count }) => (
  <div className="flex items-end justify-between mb-3 md:mb-4 gap-3">
    <div className="flex items-center gap-2">
      <Icon size={13} className="text-[#D92D20]" />
      <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#D92D20]">
        {title}
      </span>
      {typeof count === "number" && (
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#454038] ml-1">
          {String(count).padStart(2, "0")}
        </span>
      )}
    </div>
    {subtitle && (
      <span className="hidden sm:inline font-mono text-[10px] text-[#7B7168]">
        {subtitle}
      </span>
    )}
  </div>
);

const DossierRow = ({ items }) => (
  <ul
    data-testid="connections-dossiers"
    className="grid grid-cols-1 md:grid-cols-2 gap-3"
  >
    {items.map((d) => (
      <li key={d.slug} data-testid={`connections-dossier-${d.slug}`}>
        <Link
          to={`/topic/${d.slug}`}
          className="group flex items-start gap-3 border p-4 hover:border-[#D92D20] transition-colors"
          style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
        >
          <div className="flex-1 min-w-0">
            <div className="text-[14px] text-[#E4DFD3] font-heading leading-tight group-hover:text-[#D92D20] transition-colors">
              {d.title}
            </div>
            {d.note && (
              <div className="text-[11px] font-mono text-[#A39E93] mt-1.5 line-clamp-2">
                {d.note}
              </div>
            )}
            {/* Oracle-derived leads carry a `shared` array explaining
                WHY this dossier was surfaced — makes the panel feel
                like an investigation, not a random carousel. */}
            {Array.isArray(d.shared) && d.shared.length > 0 && (
              <div
                data-testid={`connections-dossier-lead-${d.slug}`}
                className="mt-2 flex flex-wrap gap-1.5"
              >
                {d.shared.slice(0, 3).map((s, i) => (
                  <span
                    key={i}
                    className="inline-block font-mono text-[9px] uppercase tracking-[0.18em] text-[#7B7168] border-l pl-1.5"
                    style={{ borderColor: "#3A3630" }}
                  >
                    {s}
                  </span>
                ))}
              </div>
            )}
          </div>
          <ArrowUpRight
            size={13}
            className="text-[#454038] group-hover:text-[#D92D20] shrink-0 mt-0.5 transition-colors"
          />
        </Link>
      </li>
    ))}
  </ul>
);

const EventRow = ({ items }) => (
  <ol
    data-testid="connections-events"
    className="border-l pl-5 space-y-3"
    style={{ borderColor: "#2D2A26" }}
  >
    {items.map((e, i) => (
      <li key={`${e.slug}-${i}`} className="relative">
        <span
          className="absolute -left-[27px] top-1.5 inline-block w-2 h-2 rounded-full"
          style={{ background: "#D92D20" }}
        />
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D92D20]">
          {e.year}
        </div>
        <div className="text-[13px] text-[#E4DFD3] leading-snug mt-0.5">{e.event}</div>
        <Link
          to={`/topic/${e.slug}`}
          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20] transition-colors mt-1"
          data-testid={`connections-event-${e.slug}-${i}`}
        >
          {e.title} <ArrowUpRight size={11} />
        </Link>
      </li>
    ))}
  </ol>
);

const _slugify = (name) =>
  (name || "")
    .toLowerCase()
    .replace(/\([^)]*\)/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const NodeChips = ({ items, kind, testid, basePath }) => (
  <ul data-testid={testid} className="flex flex-wrap gap-2">
    {items.map((p) => (
      <li key={p.name}>
        <Link
          to={`${basePath}/${_slugify(p.name)}`}
          className="inline-flex items-center gap-2 border px-3 py-1.5 font-mono text-[11px] text-[#E4DFD3] hover:border-[#D92D20] hover:text-[#D92D20] transition-colors"
          style={{ borderColor: "#3A3630", background: "#0F0E12" }}
          data-testid={`connections-${kind}-${_slugify(p.name)}`}
        >
          {p.name}
          <span
            className="text-[9px] font-mono text-[#7B7168] tracking-[0.15em]"
            title={`Appears in ${p.count} dossiers`}
          >
            ×{p.count}
          </span>
        </Link>
      </li>
    ))}
  </ul>
);

export const ConnectionsPanel = ({ editorialModules }) => {
  const conn = editorialModules?.connections || null;
  if (!conn) return null;

  const dossiers = (conn.dossiers || []).slice(0, 4);
  const events   = (conn.events   || []).slice(0, 6);
  const people   = (conn.people   || []).slice(0, 8);
  const orgs     = (conn.organizations || []).slice(0, 8);

  if (dossiers.length === 0 && events.length === 0 && people.length === 0 && orgs.length === 0) {
    return null;
  }

  return (
    <section
      data-testid="dossier-connections-panel"
      className="mt-16 md:mt-20 pt-10 md:pt-12 border-t"
      style={{ borderColor: "#2D2A26" }}
    >
      {/* Section header */}
      <div className="flex items-center gap-3 mb-6 md:mb-8">
        <span className="h-px w-10" style={{ background: "#D92D20" }} />
        <div className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#D92D20]">
          Continue Your Investigation
        </div>
      </div>
      <h2 className="font-display text-2xl md:text-4xl text-[#E4DFD3] leading-tight tracking-tight mb-8 md:mb-10">
        This dossier does not end here.
      </h2>

      <div className="space-y-10 md:space-y-12">
        {dossiers.length > 0 && (
          <div>
            <RailHeader
              icon={Compass}
              title="Related Investigations"
              subtitle="Documented relationships"
              count={dossiers.length}
            />
            <DossierRow items={dossiers} />
          </div>
        )}

        {events.length > 0 && (
          <div>
            <RailHeader
              icon={Clock}
              title="Timeline Connections"
              subtitle="From connected investigations"
              count={events.length}
            />
            <EventRow items={events} />
          </div>
        )}

        {people.length > 0 && (
          <div>
            <RailHeader
              icon={Users}
              title="Key People Appearing in Multiple Dossiers"
              count={people.length}
            />
            <NodeChips items={people} kind="people" testid="connections-people" basePath="/people" />
          </div>
        )}

        {orgs.length > 0 && (
          <div>
            <RailHeader
              icon={Building2}
              title="Organizations & Agencies"
              count={orgs.length}
            />
            <NodeChips items={orgs} kind="orgs" testid="connections-orgs" basePath="/organizations" />
          </div>
        )}
      </div>
    </section>
  );
};

export default ConnectionsPanel;
